/** Heat Loss Calculator - Ported from Magento 2 PHP module: Crimson\HeatLossCalculator */

class HeatLossCalculator {
    constructor() {
        this.form = null;
        this.lastAutoCalculateState = false;
        this.init();
    }

    init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setup());
        } else {
            this.setup();
        }
    }

    setup() {
        this.form = document.querySelector('#heatloss-calculator-form') || document.querySelector('.heatloss-calculator-form');
        if (!this.form) {
            console.warn('Heat Loss Calculator: Form not found');
            return;
        }

        this.initializeEventHandlers();
    }

    // Unit conversion functions
    fahrenheitToCelsius(f) {
        return (f - 32) * 5 / 9;
    }

    inchesToMeters(inches) {
        return inches / 39.3701;
    }

    feetToMeters(feet) {
        return feet / 3.28084;
    }

    millimetersToMeters(mm) {
        return mm / 1000;
    }

    lbPerFt3ToKgPerM3(value) {
        return value / 0.062428;
    }

    gPerCm3ToKgPerM3(value) {
        return value / 0.001;
    }

    jPerGCToJPerKgC(value) {
        return value / 0.001;
    }

    btuPerLbFToJPerKgC(value) {
        return value / 0.000238846;
    }

    hoursToSeconds(hours) {
        return hours * 3600;
    }

    minutesToSeconds(minutes) {
        return minutes * 60;
    }

    ft3PerMinToM3PerS(value) {
        return value / 2118.88;
    }

    m3PerMinToM3PerS(value) {
        return value / 60;
    }

    litersPerMinToM3PerS(value) {
        return value / 60000;
    }

    gallonsPerMinToM3PerS(value) {
        return value / 15850.3;
    }

    ftPerSToMPerS(value) {
        return value / 3.2808;
    }

    mphToMPerS(value) {
        return value / 2.23694;
    }

    kmPerHToMPerS(value) {
        return value / 3.6;
    }

    /**
     * Generic unit converter - converts to SI units
     * @param {number} value - Value to convert
     * @param {number} unitCode - Unit code (0, 1, 2, 3...)
     * @param {string} category - Category (temp, size, density, specificHeat, time, flowRate, windSpeed)
     * @returns {number} - Converted value in SI units
     */
    convertToSI(value, unitCode, category) {
        const unit = parseInt(unitCode);

        switch (category) {
            case 'temp':
                // 0=Fahrenheit, 1=Celsius
                return unit === 0 ? this.fahrenheitToCelsius(value) : value;

            case 'size':
                // 0=inches, 1=meters, 2=millimeters, 3=feet
                switch (unit) {
                    case 0:
                        return this.inchesToMeters(value);
                    case 1:
                        return value;
                    case 2:
                        return this.millimetersToMeters(value);
                    case 3:
                        return this.feetToMeters(value);
                    default:
                        return value;
                }

            case 'density':
                // 0=kg/m³, 1=lb/ft³, 2=g/cm³
                switch (unit) {
                    case 0:
                        return value;
                    case 1:
                        return this.lbPerFt3ToKgPerM3(value);
                    case 2:
                        return this.gPerCm3ToKgPerM3(value);
                    default:
                        return value;
                }

            case 'specificHeat':
                // 0=J/kg°C, 1=J/g°C, 2=BTU/lb°F
                switch (unit) {
                    case 0:
                        return value;
                    case 1:
                        return this.jPerGCToJPerKgC(value);
                    case 2:
                        return this.btuPerLbFToJPerKgC(value);
                    default:
                        return value;
                }

            case 'time':
                // 0=hours, 1=minutes
                return unit === 0 ? this.hoursToSeconds(value) : this.minutesToSeconds(value);

            case 'flowRate':
                // 0=ft³/min, 1=m³/min, 2=L/min, 3=gal/min
                switch (unit) {
                    case 0:
                        return this.ft3PerMinToM3PerS(value);
                    case 1:
                        return this.m3PerMinToM3PerS(value);
                    case 2:
                        return this.litersPerMinToM3PerS(value);
                    case 3:
                        return this.gallonsPerMinToM3PerS(value);
                    default:
                        return value;
                }

            case 'windSpeed':
                // 0=mph, 1=km/h, 2=ft/s, 3=m/s
                switch (unit) {
                    case 0:
                        return this.mphToMPerS(value);
                    case 1:
                        return this.kmPerHToMPerS(value);
                    case 2:
                        return this.ftPerSToMPerS(value);
                    case 3:
                        return value;
                    default:
                        return value;
                }

            default:
                return value;
        }
    }

    // Normalize all form inputs to SI units
    normalizeInputs(formData) {
        // Handle custom vessel content fields
        const isCustomContent = formData.vesselContent === 'other';
        const vesselContentSpecificHeat = isCustomContent
            ? (formData.vesselContentSpecificHeatOther || '0')
            : (formData.vesselContentSpecificHeat || '0');
        const vesselContentSpecificHeatUnit = isCustomContent
            ? (formData.vesselContentSpecificHeatUnitOther || '0')
            : (formData.vesselContentSpecificHeatUnit || '0');
        const vesselContentDensity = isCustomContent
            ? (formData.vesselContentDensityOther || '0')
            : (formData.vesselContentDensity || '0');
        const vesselContentDensityUnit = isCustomContent
            ? (formData.vesselContentDensityUnitOther || '0')
            : (formData.vesselContentDensityUnit || '0');

        // Handle custom vessel material fields
        const isCustomMaterial = formData.vesselMaterial === 'other';
        const vesselMaterialSpecificHeat = isCustomMaterial
            ? (formData.vesselMaterialSpecificHeatOther || '0')
            : (formData.vesselMaterialSpecificHeat || '0');
        const vesselMaterialSpecificHeatUnit = isCustomMaterial
            ? (formData.vesselMaterialSpecificHeatUnitOther || '0')
            : (formData.vesselMaterialSpecificHeatUnit || '0');
        const vesselMaterialDensity = isCustomMaterial
            ? (formData.vesselMaterialDensityOther || '0')
            : (formData.vesselMaterialDensity || '0');
        const vesselMaterialDensityUnit = isCustomMaterial
            ? (formData.vesselMaterialDensityUnitOther || '0')
            : (formData.vesselMaterialDensityUnit || '0');

        const meltingPhaseCheckbox = this.form.querySelector('[name="meltingPhaseChange"]');
        const meltingPhaseChange = meltingPhaseCheckbox ? meltingPhaseCheckbox.value === '1' : false;

        return {
            vesselContent: formData.vesselContent,
            vesselContentSpecificHeat: this.convertToSI(
                parseFloat(vesselContentSpecificHeat) || 0,
                vesselContentSpecificHeatUnit,
                'specificHeat'
            ),
            vesselContentDensity: this.convertToSI(
                parseFloat(vesselContentDensity) || 0,
                vesselContentDensityUnit,
                'density'
            ),
            vesselContentPhase: parseFloat(formData.vesselContentPhase || 0),
            meltingPhaseChange: meltingPhaseChange,

            vesselMaterial: formData.vesselMaterial,
            vesselMaterialSpecificHeat: this.convertToSI(
                parseFloat(vesselMaterialSpecificHeat) || 0,
                vesselMaterialSpecificHeatUnit,
                'specificHeat'
            ),
            vesselMaterialDensity: this.convertToSI(
                parseFloat(vesselMaterialDensity) || 0,
                vesselMaterialDensityUnit,
                'density'
            ),

            ambientTemp: this.convertToSI(
                parseFloat(formData.ambientTemperature),
                formData.ambientTemperatureUnit,
                'temp'
            ),
            operatingTemp: this.convertToSI(
                parseFloat(formData.operatingTemperature),
                formData.operatingTemperatureUnit,
                'temp'
            ),
            initialTemp: this.convertToSI(
                parseFloat(formData.initialTemperature),
                formData.initialTemperatureUnit,
                'temp'
            ),

            geometry: formData.vesselGeometry,
            diameter: formData.vesselDiameter ? this.convertToSI(
                parseFloat(formData.vesselDiameter),
                formData.vesselDiameterUnit,
                'size'
            ) : 0,
            width: formData.vesselWidth ? this.convertToSI(
                parseFloat(formData.vesselWidth),
                formData.vesselWidthUnit,
                'size'
            ) : 0,
            length: formData.vesselLength ? this.convertToSI(
                parseFloat(formData.vesselLength),
                formData.vesselLengthUnit,
                'size'
            ) : 0,
            height: this.convertToSI(
                parseFloat(formData.vesselHeight),
                formData.vesselHeightUnit,
                'size'
            ),
            wallThickness: formData.vesselWallThickness ? this.convertToSI(
                parseFloat(formData.vesselWallThickness),
                formData.vesselWallThicknessUnit,
                'size'
            ) : 0,
            fillPercentage: parseFloat(formData.contentFillPercentage),

            flowRate: this.convertToSI(
                parseFloat(formData.flowRate || 0),
                formData.flowRateUnit,
                'flowRate'
            ),
            heatupTime: this.convertToSI(
                parseFloat(formData.heatupTime || 0),
                formData.heatupTimeUnit,
                'time'
            ),
            windVelocity: this.convertToSI(
                parseFloat(formData.windVelocity),
                formData.windVelocityUnit,
                'windSpeed'
            ),

            insulType: formData.insulationType || '',
            insulCoeff: parseFloat(formData.insulationTypeCoefficient || 0),
            insulExp: parseFloat(formData.insulationTypeExponent || 0),
            insulThick: formData.insulationThickness ? this.convertToSI(
                parseFloat(formData.insulationThickness),
                formData.insulationThicknessUnit,
                'size'
            ) : 0,
            insulSurface: parseInt(formData.insulationSurface || 0)
        };
    }

    // Fluid volume calculations
    calculateFluidVolume(request, response) {
        const {geometry, diameter, width, length, height, wallThickness} = request;

        switch (geometry) {
            case 'cylindrical':
                response.fluidVolume = this.calculateFluidVolumeCylindrical(diameter, wallThickness, height);
                response.vesselVolume = this.calculateVesselVolumeCylindrical(diameter, height);
                if (response.fluidVolume <= 0) {
                    throw new Error('No room for fluid. Wall thickness is larger than dimensions.');
                }
                break;

            case 'pipe':
                response.fluidVolume = this.calculateFluidVolumeCylindrical(diameter, wallThickness, height);
                response.vesselVolume = this.calculateVesselVolumeCylindrical(diameter, height);
                break;

            case 'rectangle':
                response.fluidVolume = this.calculateFluidVolumeRectangle(length, width, height, wallThickness);
                response.vesselVolume = this.calculateVesselVolumeRectangle(length, width, height);
                if (response.fluidVolume <= 0) {
                    throw new Error('No room for fluid. Wall thickness is larger than dimensions.');
                }
                break;

            default:
                throw new Error('Invalid vessel geometry type');
        }
    }

    /**
     * Calculates the fluid volume of a cylindrical container taking into account its wall thickness.
     *
     * @param {number} diameter - The external diameter of the cylinder.
     * @param {number} wallThickness - The thickness of the cylinder walls.
     * @param {number} height - The external height of the cylinder.
     * @return {number} The fluid volume within the cylindrical container. Returns -1 if the wall thickness is too large to allow fluid storage.
     */
    calculateFluidVolumeCylindrical(diameter, wallThickness, height) {
        if (2 * wallThickness >= height || 2 * wallThickness >= diameter) {
            return -1;
        }
        return Math.PI * Math.pow((diameter - 2 * wallThickness) / 2, 2) * (height - 2 * wallThickness);
    }

    /**
     * Calculates the volume of a cylindrical vessel based on its diameter and height.
     *
     * @param {number} diameter - The diameter of the cylindrical vessel.
     * @param {number} height - The height of the cylindrical vessel.
     * @return {number} The calculated volume of the cylindrical vessel.
     */
    calculateVesselVolumeCylindrical(diameter, height) {
        return Math.PI * Math.pow(diameter / 2, 2) * height;
    }

    /**
     * Calculates the fluid volume of a rectangular container, accounting for wall thickness.
     *
     * @param {number} length - The external length of the container.
     * @param {number} width - The external width of the container.
     * @param {number} height - The external height of the container.
     * @param {number} wallThickness - The thickness of the walls of the container.
     * @return {number} The internal fluid volume of the container or -1 if the wall thickness is too large.
     */
    calculateFluidVolumeRectangle(length, width, height, wallThickness) {
        if (2 * wallThickness >= height || 2 * wallThickness >= width || 2 * wallThickness >= length) {
            return -1;
        }
        return (length - 2 * wallThickness) * (width - 2 * wallThickness) * (height - 2 * wallThickness);
    }

    calculateVesselVolumeRectangle(length, width, height) {
        return length * width * height;
    }

    calculateVessel(request, response) {
        switch (request.geometry) {
            case 'cylindrical':
                this.calculateCylindrical(request, response);
                break;
            case 'pipe':
                this.calculatePipe(request, response);
                break;
            case 'rectangle':
                this.calculateRectangle(request, response);
                break;
        }
    }

    calculateCylindrical(request, response) {
        const {diameter, height, insulThick, insulSurface} = request;

        const vesselSideSA = Math.PI * diameter * height;
        const vesselTopSA = Math.PI * Math.pow(diameter / 2, 2);
        const vesselCompSA = vesselSideSA + 2 * vesselTopSA;

        const insulSideSA = Math.PI * (diameter + 2 * insulThick) * (height + 2 * insulThick);
        const insulTopSA = Math.PI * Math.pow((diameter + 2 * insulThick) / 2, 2);
        const insulCompSA = insulSideSA + 2 * insulTopSA;

        this.calculateCommonGeometry(response, {
            insulSurface,
            vesselCompSA,
            insulSideSA,
            vesselSideSA,
            insulTopSA,
            vesselTopSA,
            insulCompSA,
            geoLength: height
        });
    }

    calculatePipe(request, response) {
        const {diameter, height, insulThick, insulSurface} = request;

        const vesselCompSA = Math.PI * diameter * height + 2 * Math.PI * Math.pow(diameter / 2, 2);
        const insulCompSA = Math.PI * (diameter + 2 * insulThick) * (height + 2 * insulThick);

        this.calculateCommonGeometry(response, {
            insulSurface,
            vesselCompSA,
            insulSideSA: insulCompSA,
            vesselSideSA: vesselCompSA,
            insulTopSA: 0,
            vesselTopSA: 0,
            insulCompSA,
            geoLength: height
        });
    }

    calculateCommonGeometry(response, params) {
        const {vesselVolume, fluidVolume} = response;
        const vesselWV = vesselVolume - fluidVolume;

        const sa = this.getSurfaceAreas(
            params.insulSurface,
            params.vesselCompSA,
            params.insulSideSA,
            params.vesselSideSA,
            params.insulTopSA,
            params.vesselTopSA,
            params.insulCompSA
        );

        response.vesselSA = Math.max(0, sa.vesselSA);
        response.insulSA = Math.max(0, sa.insulSA);
        response.vesselWV = Math.max(0, vesselWV);
        response.geoLength = params.geoLength;
        response.insulTopSA = params.insulTopSA;
    }

    calculateRectangle(request, response) {
        const {length, width, height, insulThick, insulSurface} = request;
        const {vesselVolume, fluidVolume} = response;

        const vesselWV = vesselVolume - fluidVolume;

        const vesselCompSA = 2 * (length * width + length * height + width * height);
        const insulCompSA = 2 * (
            (length + 2 * insulThick) * (width + 2 * insulThick) +
            (length + 2 * insulThick) * (height + 2 * insulThick) +
            (width + 2 * insulThick) * (height + 2 * insulThick)
        );

        const vesselTopSA = width * length;
        const insulTopSA = (width + 2 * insulThick) * (length + 2 * insulThick);
        const vesselSideSA = 2 * height * (width + length);
        const insulSideSA = 2 * (height + 2 * insulThick) * ((width + 2 * insulThick) + (length + 2 * insulThick));
        const geoLength = length;

        const sa = this.getSurfaceAreas(
            insulSurface,
            vesselCompSA,
            insulSideSA,
            vesselSideSA,
            insulTopSA,
            vesselTopSA,
            insulCompSA
        );

        response.vesselSA = Math.max(0, sa.vesselSA);
        response.insulSA = Math.max(0, sa.insulSA);
        response.vesselWV = Math.max(0, vesselWV);
        response.geoLength = geoLength;
        response.insulTopSA = insulTopSA;
    }

    getSurfaceAreas(insulSurface, vesselCompSA, insulSideSA, vesselSideSA, insulTopSA, vesselTopSA, insulCompSA) {
        // insulSurface: 0=None, 1=Sides, 2=Sides & Top, 3=Complete
        switch (insulSurface) {
            case 0:
                return {insulSA: 0, vesselSA: vesselCompSA};
            case 1:
                return {insulSA: insulSideSA, vesselSA: vesselCompSA - vesselSideSA};
            case 2:
                return {insulSA: insulSideSA + insulTopSA, vesselSA: vesselCompSA - vesselTopSA - vesselSideSA};
            case 3:
                return {insulSA: insulCompSA, vesselSA: 0};
            default:
                return {insulSA: 0, vesselSA: 0};
        }
    }

    /**
     * Calculates the equivalent thermal resistance and updates the response object with thermal properties such as surface temperature
     * and resistance values based on the given input parameters.
     *
     * @param {Object} request - Object containing input parameters required for the calculation.
     * @param {number} request.ambientTemp - The ambient temperature outside the system.
     * @param {number} request.operatingTemp - The operating temperature of the system.
     * @param {number} request.insulCoeff - The insulation material's thermal conductivity coefficient.
     * @param {number} request.insulExp - The insulation material's thermal conductivity exponent.
     * @param {string} request.geometry - The geometry of the system, e.g., "cylindrical", "pipe", or "rectangular".
     * @param {number} request.insulSurface - Insulation surface condition (e.g., full coverage or partial).
     * @param {number} request.insulThick - Thickness of the insulation material.
     * @param {number} request.diameter - Diameter of the cylindrical geometry (if applicable).
     * @param {number} request.windVelocity - Velocity of the wind which may influence heat transfer.
     *
     * @param {Object} response - Object to be updated with the calculated results.
     * @param {number} response.insulTopSA - Surface area of the top of the insulation (if applicable).
     * @param {number} response.insulSA - Surface area of the insulation material.
     * @param {number} response.geoLength - Length of the system's geometry (if applicable).
     * @param {number} response.vesselSA - Surface area of the vessel/system without insulation.
     *
     * @returns {void} The function updates the response object with the following properties:
     * - `response.surfaceTempC` {number}: The calculated surface temperature in degrees Celsius.
     * - `response.surfaceTempF` {number}: The calculated surface temperature in degrees Fahrenheit.
     * - `response.equivalentResistance` {number}: The calculated equivalent thermal resistance.
     * - `response.isInsulation` {number}: Indicates whether insulation is applied (1 for true, 0 for false).
     */
    calculateEquivResistance(request, response) {
        const {
            ambientTemp, operatingTemp, insulCoeff, insulExp, geometry, insulSurface,
            insulThick, diameter, windVelocity
        } = request;
        const {insulTopSA, insulSA, geoLength, vesselSA} = response;

        let equivResistance = 0;
        let surfTemp = ambientTemp;
        let insulBool = 0;

        // 10-iteration convergence algorithm
        for (let i = 0; i < 10; i++) {
            // Calculate insulation temperature (average of surface and operating)
            const insulTemp = (surfTemp + operatingTemp) / 2;

            // Thermal conductivity from coefficient/exponent
            const insulConduct = insulCoeff * Math.exp(insulExp * insulTemp);

            // Thermal resistance of insulation
            let tri = 0;

            if (geometry === 'cylindrical' || geometry === 'pipe') {
                if (insulConduct && geoLength && insulThick && diameter) {
                    let cylTemp = 0;

                    if (geometry === 'cylindrical') {
                        if (insulSurface === 3) { // Complete
                            cylTemp = (insulTopSA * 2 * insulConduct) / insulThick;
                        } else if (insulSurface === 2) { // Sides & Top
                            cylTemp = (insulTopSA * insulConduct) / insulThick;
                        }
                    }

                    tri = 1 / (1 / (Math.log((2 * insulThick + diameter) / diameter) /
                        (2 * Math.PI * insulConduct * geoLength)) + cylTemp);
                }
            } else {
                // Rectangle geometry
                if (insulConduct && insulSA) {
                    tri = insulThick / (insulConduct * insulSA);
                }
            }

            // Heat transfer coefficient (wind effect)
            let HTCoeff = 20;
            if (windVelocity > 0) {
                HTCoeff = Math.max(20, 5.5329 * Math.log(windVelocity) + 19.071);
            }

            // Calculate resistances
            const trafi = insulSA > 0 ? 1 / (HTCoeff * insulSA) : 0;
            const trafu = vesselSA > 0 ? 1 / (HTCoeff * vesselSA) : 0;

            // Combined resistance calculation
            if (insulSA > 0 && vesselSA > 0) {
                if (insulThick <= 0) {
                    equivResistance = trafu;
                } else {
                    equivResistance = 1 / ((1 / trafu) + (1 / (tri + trafi)));
                }
            } else if (insulSA <= 0 && vesselSA > 0) {
                equivResistance = trafu;
            } else if (insulSA > 0 && vesselSA <= 0) {
                if (tri > 0) {
                    equivResistance = trafi + tri;
                } else {
                    equivResistance = 0;
                }
            } else {
                equivResistance = 0;
            }

            // Calculate surface temperature
            if (trafi && tri) {
                surfTemp = operatingTemp - (operatingTemp - ambientTemp) * (tri / (tri + trafi));
                insulBool = 1;
            } else {
                break;
            }
        }

        response.surfaceTempC = Math.round(surfTemp);
        response.surfaceTempF = Math.round(surfTemp * 9 / 5 + 32);
        response.equivalentResistance = equivResistance;
        response.isInsulation = insulBool;
    }

    // Wattage calculations
    calculateMaintainWattage(request, response) {
        const {equivalentResistance} = response;

        if (equivalentResistance <= 0) {
            response.maintainWattage = 0;
            return;
        }

        const maintainWattage = (request.operatingTemp - request.ambientTemp) / equivalentResistance;
        response.maintainWattage = Math.round(maintainWattage);
    }

    calculateHeatUpWattage(request, response) {
        const {vesselVolume, fluidVolume, equivalentResistance} = response;
        const {
            vesselMaterialDensity, vesselContentDensity, fillPercentage, heatupTime,
            meltingPhaseChange, vesselContentPhase, vesselContentSpecificHeat,
            vesselMaterialSpecificHeat, flowRate, operatingTemp, ambientTemp, initialTemp
        } = request;

        const vesselWeight = vesselVolume * vesselMaterialDensity;
        const fluidWeight = fluidVolume * vesselContentDensity * (fillPercentage / 100);

        const phase = meltingPhaseChange ? vesselContentPhase : 0;
        const phaseWattage = heatupTime !== 0 ? (phase * fluidWeight) / heatupTime : 0;

        const constHU = (fluidWeight * vesselContentSpecificHeat + vesselWeight * vesselMaterialSpecificHeat) *
            equivalentResistance;

        const expFigure = constHU === 0 ? 0 : Math.exp(-1 * heatupTime / constHU);

        if (expFigure === 1 || flowRate > 0) {
            response.heatUpWattage = 0;
        } else if (equivalentResistance > 0 && constHU > 0) {
            const heatUpWattage = (operatingTemp - ambientTemp - (initialTemp - ambientTemp) * expFigure) /
                equivalentResistance / (1 - expFigure);
            response.heatUpWattage = Math.round(heatUpWattage + phaseWattage);
        } else {
            response.heatUpWattage = 0;
        }
    }

    calculateFlowWattage(request, response) {
        const {
            flowRate, vesselContentDensity, operatingTemp, initialTemp,
            vesselContentSpecificHeat, ambientTemp
        } = request;
        const {equivalentResistance} = response;

        if (flowRate <= 0 || equivalentResistance <= 0) {
            response.flowWattage = 0;
            return;
        }

        const massRate = vesselContentDensity * flowRate;
        const heatReq = (operatingTemp - initialTemp) * massRate * vesselContentSpecificHeat;
        const flowWattage = ((operatingTemp - ambientTemp) / equivalentResistance) + heatReq;
        response.flowWattage = Math.round(flowWattage);
    }

    // Main calculation pipeline
    calculate(formData) {
        try {
            const request = this.normalizeInputs(formData);

            const response = {};

            this.calculateFluidVolume(request, response);
            this.calculateVessel(request, response);
            this.calculateEquivResistance(request, response);
            this.calculateMaintainWattage(request, response);
            this.calculateHeatUpWattage(request, response);
            this.calculateFlowWattage(request, response);

            return response;
        } catch (error) {
            throw new Error(`Calculation error: ${error.message}`);
        }
    }

    /**
     * Initializes event handlers for various form elements and actions.
     * Sets up listeners for changes, input, blur, and submit events to handle form validations, updates, and interactions.
     *
     * @return {void} No return value; this method sets up event listeners and interacts with the form elements directly.
     */
    initializeEventHandlers() {
        const vesselContentSelect = this.form.querySelector('[name="vesselContent"]');
        if (vesselContentSelect) {
            vesselContentSelect.addEventListener('change', (e) => this.handleVesselContentChange(e));
        }

        const vesselMaterialSelect = this.form.querySelector('[name="vesselMaterial"]');
        if (vesselMaterialSelect) {
            vesselMaterialSelect.addEventListener('change', (e) => this.handleVesselMaterialChange(e));
        }

        const geometrySelect = this.form.querySelector('[name="vesselGeometry"]');
        if (geometrySelect) {
            geometrySelect.addEventListener('change', (e) => this.handleGeometryChange(e));
        }

        const insulationSelect = this.form.querySelector('[name="insulationType"]');
        if (insulationSelect) {
            insulationSelect.addEventListener('change', (e) => this.handleInsulationChange(e));
        }

        const wallThicknessInput = this.form.querySelector('[name="vesselWallThickness"]');
        const diameterInput = this.form.querySelector('[name="vesselDiameter"]');
        const diameterUnitSelect = this.form.querySelector('[name="vesselDiameterUnit"]');
        const wallThicknessUnitSelect = this.form.querySelector('[name="vesselWallThicknessUnit"]');
        [wallThicknessInput, diameterInput].filter(Boolean).forEach(input => {
            input.addEventListener('blur', () => this.validatePipeWallThickness());
        });
        [diameterUnitSelect, wallThicknessUnitSelect].filter(Boolean).forEach(select => {
            select.addEventListener('change', () => this.validatePipeWallThickness());
        });

        const allInputs = this.form.querySelectorAll('input, select');
        allInputs.forEach(input => {
            input.addEventListener('blur', (e) => {
                const isValid = this.validateField(e.target);
                if (!isValid) {
                    this.showFieldError(e.target, 'This is a required field.');
                } else {
                    this.clearFieldError(e.target);
                }
                this.validateSection(e.target);
                this.checkAndAutoCalculate();
            });

            input.addEventListener('input', (e) => {
                const isValid = this.validateField(e.target);
                if (!isValid) {
                    this.showFieldError(e.target, 'This is a required field.');
                } else {
                    this.clearFieldError(e.target);
                }
                this.validateSection(e.target);
                this.lastAutoCalculateState = false;
                this.checkAndAutoCalculate();
            });

            input.addEventListener('change', (e) => {
                this.validateSection(e.target);
                if (['initialTemperature', 'operatingTemperature', 'initialTemperatureUnit', 'operatingTemperatureUnit'].includes(e.target.name)) {
                    this.updateHeatUpTimeVisibility();
                    this.validateSection(e.target);
                }
                this.checkAndAutoCalculate();
            });
        });

        this.updateHeatUpTimeVisibility();

        this.form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.handleSubmit(e);
        });

        const calculateButton = this.form.querySelector('#calculate-button');
        if (calculateButton) {
            calculateButton.addEventListener('click', (e) => this.handleSubmit(e));
        }

        const printButton = this.form.querySelector('#print-button');
        if (printButton) {
            printButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.handlePrint();
            });
        }
    }

    /**
     * Handles changes to the vessel content selection by updating the display, input fields, and other
     * related elements based on the selected value.
     *
     * @param {Event} e The event object associated with the change event on the vessel content selection input.
     * @return {void} This method does not return a value.
     */
    handleVesselContentChange(e) {
        const selected = e.target.value;
        const customSpecificHeat = this.form.querySelector('#vessel-content-custom-specific-heat');
        const customDensity = this.form.querySelector('#vessel-content-custom-density');
        const displaySpecificHeat = this.form.querySelector('#vessel-content-display-specific-heat');
        const displayDensity = this.form.querySelector('#vessel-content-display-density');
        const meltingPhase = this.form.querySelector('#melting-phase');

        if (selected === 'other') {
            if (customSpecificHeat) customSpecificHeat.style.display = 'flex';
            if (customDensity) customDensity.style.display = 'flex';
            if (displaySpecificHeat) displaySpecificHeat.style.display = 'none';
            if (displayDensity) displayDensity.style.display = 'none';
        } else {
            if (customSpecificHeat) customSpecificHeat.style.display = 'none';
            if (customDensity) customDensity.style.display = 'none';
            if (displaySpecificHeat) displaySpecificHeat.style.display = 'flex';
            if (displayDensity) displayDensity.style.display = 'flex';

            const option = e.target.selectedOptions[0];
            if (option) {
                const specificHeat = option.dataset.specificHeat || option.getAttribute('data-specific-heat');
                const density = option.dataset.density || option.getAttribute('data-density');
                const phase = option.dataset.phase || option.getAttribute('data-phase') || '0';

                const specificHeatInput = this.form.querySelector('[name="vesselContentSpecificHeat"]');
                const densityInput = this.form.querySelector('[name="vesselContentDensity"]');
                const phaseInput = this.form.querySelector('[name="vesselContentPhase"]');
                const specificHeatDisplay = this.form.querySelector('#vesselContentSpecificHeatDetails');
                const densityDisplay = this.form.querySelector('#vesselContentDensityDetails');

                if (specificHeatInput) specificHeatInput.value = specificHeat || '0';
                if (densityInput) densityInput.value = density || '0';
                if (phaseInput) phaseInput.value = phase || '0';
                if (specificHeatDisplay) specificHeatDisplay.textContent = parseFloat(specificHeat || 0).toFixed(1);
                if (densityDisplay) densityDisplay.textContent = parseFloat(density || 0).toFixed(1);

                if (meltingPhase) {
                    meltingPhase.style.display = (parseFloat(phase) > 0) ? 'flex' : 'none';
                }
            }
        }

        this.validateSection(e.target);
        this.checkAndAutoCalculate();
    }

    /**
     * Handles changes to the vessel material selection, adjusting the visibility and values of form elements
     * associated with custom and predefined material properties like specific heat and density.
     *
     * @param {Event} e - The event object triggered by the vessel material selection change, containing details
     * such as the selected option and its data attributes.
     * @return {void} This method does not return a value but applies updates to the DOM and triggers relevant validations.
     */
    handleVesselMaterialChange(e) {
        const selected = e.target.value;
        const customSpecificHeat = this.form.querySelector('#vessel-material-custom-specific-heat');
        const customDensity = this.form.querySelector('#vessel-material-custom-density');
        const displaySpecificHeat = this.form.querySelector('#vessel-material-display-specific-heat');
        const displayDensity = this.form.querySelector('#vessel-material-display-density');

        if (selected === 'other') {
            if (customSpecificHeat) customSpecificHeat.style.display = 'flex';
            if (customDensity) customDensity.style.display = 'flex';
            if (displaySpecificHeat) displaySpecificHeat.style.display = 'none';
            if (displayDensity) displayDensity.style.display = 'none';
        } else {
            if (customSpecificHeat) customSpecificHeat.style.display = 'none';
            if (customDensity) customDensity.style.display = 'none';
            if (displaySpecificHeat) displaySpecificHeat.style.display = 'flex';
            if (displayDensity) displayDensity.style.display = 'flex';

            const option = e.target.selectedOptions[0];
            if (option) {
                const specificHeat = option.dataset.specificHeat || option.getAttribute('data-specific-heat');
                const density = option.dataset.density || option.getAttribute('data-density');

                const specificHeatInput = this.form.querySelector('[name="vesselMaterialSpecificHeat"]');
                const densityInput = this.form.querySelector('[name="vesselMaterialDensity"]');
                const specificHeatDisplay = this.form.querySelector('#vesselMaterialSpecificHeatDetails');
                const densityDisplay = this.form.querySelector('#vesselMaterialDensityDetails');

                if (specificHeatInput) specificHeatInput.value = specificHeat || '0';
                if (densityInput) densityInput.value = density || '0';
                if (specificHeatDisplay) specificHeatDisplay.textContent = parseFloat(specificHeat || 0).toFixed(1);
                if (densityDisplay) densityDisplay.textContent = parseFloat(density || 0).toFixed(1);
            }
        }

        this.validateSection(e.target);
        this.checkAndAutoCalculate();
    }

    /**
     * Handles changes in geometry selection, updates the visibility of relevant form fields, and triggers validation.
     *
     * @param {Event} e - The event object triggered by a user interaction with the geometry selection input.
     * @return {void} This method does not return a value.
     */
    handleGeometryChange(e) {
        const geometry = e.target.value;
        const geometryFields = this.form.querySelector('#geometry-fields');
        const allGeometryControls = this.form.querySelectorAll('.geometry-control');

        allGeometryControls.forEach(control => {
            control.style.display = 'none';
            const fields = control.querySelectorAll('input, select');
            fields.forEach(field => this.clearFieldError(field));
        });

        if (geometry && geometryFields) {
            geometryFields.style.display = 'block';

            if (geometry === 'cylindrical') {
                const cylindricalFields = this.form.querySelectorAll('.cylindrical');
                cylindricalFields.forEach(field => field.style.display = 'flex');
            } else if (geometry === 'pipe') {
                const pipeFields = this.form.querySelectorAll('.pipe');
                pipeFields.forEach(field => field.style.display = 'flex');
                this.validatePipeWallThickness();
            } else if (geometry === 'rectangle') {
                const rectangleFields = this.form.querySelectorAll('.rectangle');
                rectangleFields.forEach(field => field.style.display = 'flex');
            }
        } else if (geometryFields) {
            geometryFields.style.display = 'none';
        }

        this.validateSection(e.target);
        this.checkAndAutoCalculate();
    }

    /**
     * Handles changes in the insulation selection dropdown and updates the form elements based on user selection.
     *
     * @param {Event} e - The event triggered by the user interaction with the insulation dropdown.
     * @return {void} This method does not return a value.
     */
    handleInsulationChange(e) {
        const selected = e.target.value;
        const insulationFields = this.form.querySelector('#insulation-fields');

        if (!selected || selected === '') {
            if (insulationFields) insulationFields.style.display = 'none';
        } else {
            if (insulationFields) insulationFields.style.display = 'block';

            const option = e.target.selectedOptions[0];
            if (option) {
                const coefficient = option.dataset.coefficient || option.getAttribute('data-coefficient');
                const exponent = option.dataset.exponent || option.getAttribute('data-exponent');

                const coeffInput = this.form.querySelector('[name="insulationTypeCoefficient"]');
                const expInput = this.form.querySelector('[name="insulationTypeExponent"]');

                if (coeffInput) coeffInput.value = coefficient || '0';
                if (expInput) expInput.value = exponent || '0';
            }
        }

        this.validateSection(e.target);
        this.checkAndAutoCalculate();
    }

    isFieldVisible(field) {
        if (!field) return false;

        const fieldStyle = window.getComputedStyle(field);
        if (fieldStyle.display === 'none' || fieldStyle.visibility === 'hidden') {
            return false;
        }

        let parent = field.parentElement;
        while (parent && parent !== this.form && parent !== document.body) {
            const parentStyle = window.getComputedStyle(parent);
            if (parentStyle.display === 'none' || parentStyle.visibility === 'hidden') {
                return false;
            }
            parent = parent.parentElement;
        }

        return true;
    }

    /**
     * Validates the wall thickness of a pipe based on its diameter and ensures the values meet the required constraints.
     * If the vessel geometry is not a pipe, the validation passes by default.
     * Ensures that the wall thickness is less than half of the pipe diameter.
     * Both values are normalized to SI units (meters) before comparison to handle different unit types.
     * If validation fails, an error message is displayed next to the wall thickness input field.
     *
     * @return {boolean} Returns true if the wall thickness is valid or if the validation is not applicable; otherwise, false.
     */
    validatePipeWallThickness() {
        const geometrySelect = this.form.querySelector('[name="vesselGeometry"]');
        if (!geometrySelect || geometrySelect.value !== 'pipe') return true;

        const diameterInput = this.form.querySelector('[name="vesselDiameter"]');
        const wallThicknessInput = this.form.querySelector('[name="vesselWallThickness"]');
        const diameterUnitSelect = this.form.querySelector('[name="vesselDiameterUnit"]');
        const wallThicknessUnitSelect = this.form.querySelector('[name="vesselWallThicknessUnit"]');

        if (!diameterInput || !wallThicknessInput) return true;

        const diameter = parseFloat(diameterInput.value);
        const wallThickness = parseFloat(wallThicknessInput.value);
        const diameterUnit = diameterUnitSelect ? diameterUnitSelect.value : null;
        const wallThicknessUnit = wallThicknessUnitSelect ? wallThicknessUnitSelect.value : null;

        // Normalize both values to SI units (meters) for comparison
        if (diameter && wallThickness && diameterUnit !== null && wallThicknessUnit !== null) {
            const diameterMeters = this.convertToSI(diameter, diameterUnit, 'size');
            const wallThicknessMeters = this.convertToSI(wallThickness, wallThicknessUnit, 'size');

            if (wallThicknessMeters >= diameterMeters / 2) {
                const errorMsg = 'Wall thickness cannot be greater than\nor equal to half the pipe diameter.';
                this.showFieldError(wallThicknessInput, errorMsg);
                return false;
            }
        }

        this.clearFieldError(wallThicknessInput);
        return true;
    }

    /**
     * Validates a field based on its type, value, and specific conditions such as whether it is required.
     *
     * @param {HTMLElement} field - The field element to validate. This can be an input, select, or other form field element.
     * @return {boolean} Returns true if the field is valid, otherwise false.
     */
    validateField(field) {
        if (!field) return true;

        const fieldName = field.name;
        const fieldValue = field.value;
        const trimmedValue = fieldValue ? String(fieldValue).trim() : '';

        const isRequired = (field.hasAttribute('required') ||
                field.classList.contains('required') ||
                field.closest('.required-field') !== null) &&
            fieldName !== 'insulationType';

        const fieldType = field.type || (field.tagName === 'SELECT' ? 'select' : 'text');

        if (isRequired && (!trimmedValue || trimmedValue === '')) {
            return false;
        }

        if (!trimmedValue || trimmedValue === '') {
            return !isRequired;
        }

        if (fieldType === 'text' && fieldValue) {
            const numValue = parseFloat(fieldValue);

            if (isNaN(numValue)) {
                return false;
            }

            if (['flowRate', 'vesselWallThickness', 'contentFillPercentage', 'insulationThickness'].includes(fieldName)) {
                if (numValue < 0) {
                    return false;
                }
            }

            if (fieldName === 'contentFillPercentage') {
                if (numValue < 0 || numValue > 100) {
                    return false;
                }
            }

            // Heat-up time: can be 0 if initial >= operating temp (handled in section validation)
            // Otherwise should be >= 0 (not negative)
            if (fieldName === 'heatupTime') {
                if (numValue < 0) {
                    return false;
                }
            }
        }

        if (fieldType === 'select' && isRequired && !fieldValue) {
            return false;
        }

        return true;
    }

    /**
     * Updates the visibility of the heat-up time field based on the comparison of initial
     * and operating temperatures. If the operating temperature is greater than the initial
     * temperature, the heat-up time field is shown; otherwise, it is hidden and its value
     * is cleared.
     *
     * @return {void} This method does not return anything.
     */
    updateHeatUpTimeVisibility() {
        const initialTemp = parseFloat(this.form.querySelector('[name="initialTemperature"]')?.value || 0);
        const operatingTemp = parseFloat(this.form.querySelector('[name="operatingTemperature"]')?.value || 0);
        const initialTempUnit = this.form.querySelector('[name="initialTemperatureUnit"]')?.value || '0';
        const operatingTempUnit = this.form.querySelector('[name="operatingTemperatureUnit"]')?.value || '0';

        const heatUpTimeField = document.getElementById('heat-up-time-field');
        if (!heatUpTimeField) return;

        // Convert to same units for comparison
        const initialTempC = initialTemp ? this.convertToSI(initialTemp, initialTempUnit, 'temp') : 0;
        const operatingTempC = operatingTemp ? this.convertToSI(operatingTemp, operatingTempUnit, 'temp') : 0;

        // Show heat-up time only if operating temp > initial temp
        if (operatingTempC > initialTempC && initialTemp && operatingTemp) {
            heatUpTimeField.style.display = 'flex';
        } else {
            heatUpTimeField.style.display = 'none';
            const heatupTimeInput = this.form.querySelector('[name="heatupTime"]');
            if (heatupTimeInput) {
                heatupTimeInput.value = '';
            }
        }
    }

    /**
     * Validates a specific section of the form or all sections if no specific section is targeted.
     * This method checks for required fields, specific field conditions, and performs
     * section-specific validations. Clears errors for hidden fields and updates error states
     * where applicable.
     *
     * @param {Element|null} targetElement The DOM element within the desired section to validate.
     * If null, all sections will be validated.
     * @return {boolean} True if the targeted section or all sections are valid, false otherwise.
     */
    validateSection(targetElement) {
        this.closeErrorBox();

        const section = targetElement ? targetElement.closest('.section') : null;
        if (!section) {
            return this.validateAllSections();
        }

        const marker = section.querySelector('.marker span');
        let isValid = true;

        section.querySelectorAll('input, select').forEach(field => {
            // Skip validation for hidden fields
            if (!this.isFieldVisible(field)) {
                // Clear any existing errors on hidden fields
                this.clearFieldError(field);
                return;
            }

            if (!this.validateField(field)) {
                isValid = false;
                this.showFieldError(field, 'This is a required field.');
            } else {
                this.clearFieldError(field);
            }
        });

        // Section-specific validations

        // Section 1: Vessel Content
        if (section.classList.contains('one')) {
            const vesselContent = this.form.querySelector('[name="vesselContent"]');
            if (vesselContent && vesselContent.value === 'other') {
                const customSpecificHeat = this.form.querySelector('[name="vesselContentSpecificHeatOther"]');
                const customDensity = this.form.querySelector('[name="vesselContentDensityOther"]');
                if (customSpecificHeat && !this.validateField(customSpecificHeat)) {
                    isValid = false;
                    this.showFieldError(customSpecificHeat, 'This is a required field.');
                } else if (customSpecificHeat) {
                    this.clearFieldError(customSpecificHeat);
                }
                if (customDensity && !this.validateField(customDensity)) {
                    isValid = false;
                    this.showFieldError(customDensity, 'This is a required field.');
                } else if (customDensity) {
                    this.clearFieldError(customDensity);
                }
            }
        }

        // Section 2: Vessel Material & Temperatures
        if (section.classList.contains('two')) {
            const vesselMaterial = this.form.querySelector('[name="vesselMaterial"]');
            if (vesselMaterial && vesselMaterial.value === 'other') {
                const customSpecificHeat = this.form.querySelector('[name="vesselMaterialSpecificHeatOther"]');
                const customDensity = this.form.querySelector('[name="vesselMaterialDensityOther"]');
                if (customSpecificHeat && !this.validateField(customSpecificHeat)) {
                    isValid = false;
                    this.showFieldError(customSpecificHeat, 'This is a required field.');
                } else if (customSpecificHeat) {
                    this.clearFieldError(customSpecificHeat);
                }
                if (customDensity && !this.validateField(customDensity)) {
                    isValid = false;
                    this.showFieldError(customDensity, 'This is a required field.');
                } else if (customDensity) {
                    this.clearFieldError(customDensity);
                }
            }

            const heatUpTimeField = document.getElementById('heat-up-time-field');
            if (heatUpTimeField && heatUpTimeField.style.display !== 'none') {
                const initialTemp = parseFloat(this.form.querySelector('[name="initialTemperature"]')?.value || 0);
                const operatingTemp = parseFloat(this.form.querySelector('[name="operatingTemperature"]')?.value || 0);
                const initialTempUnit = this.form.querySelector('[name="initialTemperatureUnit"]')?.value || '0';
                const operatingTempUnit = this.form.querySelector('[name="operatingTemperatureUnit"]')?.value || '0';

                const initialTempC = this.convertToSI(initialTemp, initialTempUnit, 'temp');
                const operatingTempC = this.convertToSI(operatingTemp, operatingTempUnit, 'temp');

                const heatupTimeInput = this.form.querySelector('[name="heatupTime"]');
                if (heatupTimeInput && heatupTimeInput.value.trim()) {
                    const heatupTime = parseFloat(heatupTimeInput.value);
                    if (initialTempC < operatingTempC && heatupTime <= 0) {
                        isValid = false;
                        this.showFieldError(heatupTimeInput, 'Heat-up time must be greater than 0 when initial temperature is less than operating temperature.');
                    } else {
                        this.clearFieldError(heatupTimeInput);
                    }
                } else if (heatupTimeInput && initialTempC < operatingTempC) {
                    isValid = false;
                    this.showFieldError(heatupTimeInput, 'This is a required field.');
                } else if (heatupTimeInput) {
                    this.clearFieldError(heatupTimeInput);
                }
            }
        }

        // Section 3: Geometry
        if (section.classList.contains('three')) {
            const geometry = this.form.querySelector('[name="vesselGeometry"]')?.value;

            if (geometry) {
                if (geometry === 'cylindrical' || geometry === 'pipe') {
                    const diameter = this.form.querySelector('[name="vesselDiameter"]');
                    if (diameter && this.isFieldVisible(diameter)) {
                        if (!this.validateField(diameter)) {
                            isValid = false;
                            this.showFieldError(diameter, 'This is a required field.');
                        } else {
                            this.clearFieldError(diameter);
                        }
                    } else if (diameter) {
                        this.clearFieldError(diameter);
                    }
                }

                if (geometry === 'rectangle') {
                    const width = this.form.querySelector('[name="vesselWidth"]');
                    const length = this.form.querySelector('[name="vesselLength"]');
                    if (width && this.isFieldVisible(width)) {
                        if (!this.validateField(width)) {
                            isValid = false;
                            this.showFieldError(width, 'This is a required field.');
                        } else {
                            this.clearFieldError(width);
                        }
                    } else if (width) {
                        this.clearFieldError(width);
                    }
                    if (length && this.isFieldVisible(length)) {
                        if (!this.validateField(length)) {
                            isValid = false;
                            this.showFieldError(length, 'This is a required field.');
                        } else {
                            this.clearFieldError(length);
                        }
                    } else if (length) {
                        this.clearFieldError(length);
                    }
                }

                const height = this.form.querySelector('[name="vesselHeight"]');
                if (height && this.isFieldVisible(height)) {
                    if (!this.validateField(height)) {
                        isValid = false;
                        this.showFieldError(height, 'This is a required field.');
                    } else {
                        this.clearFieldError(height);
                    }
                } else if (height) {
                    this.clearFieldError(height);
                }
            }

            if (geometry === 'pipe') {
                if (!this.validatePipeWallThickness()) {
                    isValid = false;
                }
            }
        }

        // Section 4: Wind & Insulation
        if (section.classList.contains('four')) {
            // Insulation Type is optional (can be empty for no insulation)
            // But if selected, thickness should be >= 0
            const insulationType = this.form.querySelector('[name="insulationType"]')?.value;
            if (insulationType) {
                const insulationThickness = this.form.querySelector('[name="insulationThickness"]');
                if (insulationThickness && !this.validateField(insulationThickness)) {
                    isValid = false;
                    this.showFieldError(insulationThickness, 'This is a required field.');
                } else if (insulationThickness) {
                    this.clearFieldError(insulationThickness);
                }
            }
        }

        if (marker) {
            marker.classList.remove('red', 'green');
            if (isValid) {
                marker.classList.add('green');
            } else {
                marker.classList.add('red');
            }
        }

        return isValid;
    }

    closeErrorBox() {
        const errorBox = document.querySelector('.error-control');
        if (errorBox) {
            errorBox.style.display = 'none';
        }
    }

    /**
     * Validates all sections of a form by iterating through the form's sections and performing specific field validations based on section rules.
     * This method ensures that required fields are filled, and conditional validations are satisfied.
     *
     * Checks include:
     * - Field visibility before validation.
     * - Validation of custom fields when certain options are selected.
     * - Conditional validations for specific sections like vessel geometry, vessel material, and heat-up time.
     *
     * Errors are displayed for fields failing validation, and any existing errors are cleared for valid or hidden fields.
     *
     * @return {boolean} `true` if all sections are valid, `false` if any section fails validation.
     */
    validateAllSections() {
        let allValid = true;
        const sections = this.form.querySelectorAll('.section');

        sections.forEach(section => {
            const fields = section.querySelectorAll('input, select');
            let sectionValid = true;

            fields.forEach(field => {
                // Skip validation for hidden fields
                if (!this.isFieldVisible(field)) {
                    // Clear any existing errors on hidden fields
                    this.clearFieldError(field);
                    return;
                }

                if (!this.validateField(field)) {
                    sectionValid = false;
                    this.showFieldError(field, 'This is a required field.');
                } else {
                    this.clearFieldError(field);
                }
            });

            if (section.classList.contains('one')) {
                const vesselContent = this.form.querySelector('[name="vesselContent"]');
                if (vesselContent && vesselContent.value === 'other') {
                    const customSpecificHeat = this.form.querySelector('[name="vesselContentSpecificHeatOther"]');
                    const customDensity = this.form.querySelector('[name="vesselContentDensityOther"]');
                    if (customSpecificHeat && !this.validateField(customSpecificHeat)) {
                        sectionValid = false;
                        this.showFieldError(customSpecificHeat, 'This is a required field.');
                    } else if (customSpecificHeat) {
                        this.clearFieldError(customSpecificHeat);
                    }
                    if (customDensity && !this.validateField(customDensity)) {
                        sectionValid = false;
                        this.showFieldError(customDensity, 'This is a required field.');
                    } else if (customDensity) {
                        this.clearFieldError(customDensity);
                    }
                }
            }

            if (section.classList.contains('two')) {
                const vesselMaterial = this.form.querySelector('[name="vesselMaterial"]');
                if (vesselMaterial && vesselMaterial.value === 'other') {
                    const customSpecificHeat = this.form.querySelector('[name="vesselMaterialSpecificHeatOther"]');
                    const customDensity = this.form.querySelector('[name="vesselMaterialDensityOther"]');
                    if (customSpecificHeat && !this.validateField(customSpecificHeat)) {
                        sectionValid = false;
                        this.showFieldError(customSpecificHeat, 'This is a required field.');
                    } else if (customSpecificHeat) {
                        this.clearFieldError(customSpecificHeat);
                    }
                    if (customDensity && !this.validateField(customDensity)) {
                        sectionValid = false;
                        this.showFieldError(customDensity, 'This is a required field.');
                    } else if (customDensity) {
                        this.clearFieldError(customDensity);
                    }
                }

                const heatUpTimeField = document.getElementById('heat-up-time-field');
                if (heatUpTimeField && heatUpTimeField.style.display !== 'none') {
                    const initialTemp = parseFloat(this.form.querySelector('[name="initialTemperature"]')?.value || 0);
                    const operatingTemp = parseFloat(this.form.querySelector('[name="operatingTemperature"]')?.value || 0);
                    const initialTempUnit = this.form.querySelector('[name="initialTemperatureUnit"]')?.value || '0';
                    const operatingTempUnit = this.form.querySelector('[name="operatingTemperatureUnit"]')?.value || '0';
                    const initialTempC = this.convertToSI(initialTemp, initialTempUnit, 'temp');
                    const operatingTempC = this.convertToSI(operatingTemp, operatingTempUnit, 'temp');
                    const heatupTimeInput = this.form.querySelector('[name="heatupTime"]');
                    if (heatupTimeInput && heatupTimeInput.value.trim()) {
                        const heatupTime = parseFloat(heatupTimeInput.value);
                        if (initialTempC < operatingTempC && heatupTime <= 0) {
                            sectionValid = false;
                            this.showFieldError(heatupTimeInput, 'Heat-up time must be greater than 0 when initial temperature is less than operating temperature.');
                        } else {
                            this.clearFieldError(heatupTimeInput);
                        }
                    } else if (heatupTimeInput && initialTempC < operatingTempC) {
                        sectionValid = false;
                        this.showFieldError(heatupTimeInput, 'This is a required field.');
                    } else if (heatupTimeInput) {
                        this.clearFieldError(heatupTimeInput);
                    }
                }
            }

            if (section.classList.contains('three')) {
                const geometry = this.form.querySelector('[name="vesselGeometry"]')?.value;
                if (geometry) {
                    if (geometry === 'cylindrical' || geometry === 'pipe') {
                        const diameter = this.form.querySelector('[name="vesselDiameter"]');
                        if (diameter && this.isFieldVisible(diameter)) {
                            if (!this.validateField(diameter)) {
                                sectionValid = false;
                                this.showFieldError(diameter, 'This is a required field.');
                            } else {
                                this.clearFieldError(diameter);
                            }
                        } else if (diameter) {
                            this.clearFieldError(diameter);
                        }
                    }
                    if (geometry === 'rectangle') {
                        const width = this.form.querySelector('[name="vesselWidth"]');
                        const length = this.form.querySelector('[name="vesselLength"]');
                        if (width && this.isFieldVisible(width)) {
                            if (!this.validateField(width)) {
                                sectionValid = false;
                                this.showFieldError(width, 'This is a required field.');
                            } else {
                                this.clearFieldError(width);
                            }
                        } else if (width) {
                            this.clearFieldError(width);
                        }
                        if (length && this.isFieldVisible(length)) {
                            if (!this.validateField(length)) {
                                sectionValid = false;
                                this.showFieldError(length, 'This is a required field.');
                            } else {
                                this.clearFieldError(length);
                            }
                        } else if (length) {
                            this.clearFieldError(length);
                        }
                    }
                    const height = this.form.querySelector('[name="vesselHeight"]');
                    if (height && this.isFieldVisible(height)) {
                        if (!this.validateField(height)) {
                            sectionValid = false;
                            this.showFieldError(height, 'This is a required field.');
                        } else {
                            this.clearFieldError(height);
                        }
                    } else if (height) {
                        this.clearFieldError(height);
                    }
                }
                if (geometry === 'pipe') {
                    if (!this.validatePipeWallThickness()) {
                        sectionValid = false;
                    }
                }
            }

            if (section.classList.contains('four')) {
                const insulationType = this.form.querySelector('[name="insulationType"]')?.value;
                if (insulationType) {
                    const insulationThickness = this.form.querySelector('[name="insulationThickness"]');
                    if (insulationThickness && !this.validateField(insulationThickness)) {
                        sectionValid = false;
                        this.showFieldError(insulationThickness, 'This is a required field.');
                    } else if (insulationThickness) {
                        this.clearFieldError(insulationThickness);
                    }
                }
            }

            const marker = section.querySelector('.marker span');
            if (marker) {
                marker.classList.remove('red', 'green');
                marker.classList.add(sectionValid ? 'green' : 'red');
            }

            if (!sectionValid) {
                allValid = false;
            }
        });

        return allValid;
    }

    /**
     * Checks whether all required sections are valid and triggers automatic calculation
     * if necessary. This method ensures calculation only occurs when all fields are valid
     * and prevents redundant calculations by maintaining the state of the last auto-calculation.
     *
     * @return {void} This method does not return any value.
     */
    checkAndAutoCalculate() {
        // Check if all sections are valid
        const allValid = this.validateAllSections();

        // Only calculate if all fields are valid and we haven't already calculated for this state
        if (allValid && !this.lastAutoCalculateState) {
            this.lastAutoCalculateState = true;
            this.performCalculation(true);
        } else if (!allValid) {
            this.lastAutoCalculateState = false;
        }
    }

    /**
     * Performs a calculation based on the data from a form and optionally shows a loader during the process.
     *
     * @param {boolean} showLoader - Indicates whether to display a loader while the calculation is being performed. Defaults to true.
     * @return {void} This method does not return a value but updates the state by displaying results or showing errors.
     */
    performCalculation(showLoader = true) {
        const MIN_LOADER_TIME = 350;
        const startTime = Date.now();

        if (showLoader) {
            this.showLoader();
            void this.form.offsetHeight;
        }

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                try {
                    const formData = new FormData(this.form);
                    const data = Object.fromEntries(formData.entries());
                    const results = this.calculate(data);

                    if (showLoader) {
                        const elapsed = Date.now() - startTime;
                        const remainingTime = Math.max(0, MIN_LOADER_TIME - elapsed);
                        setTimeout(() => {
                            this.displayResults(results);
                            this.hideLoader();
                        }, remainingTime);
                    } else {
                        this.displayResults(results);
                    }
                } catch (error) {
                    if (showLoader) {
                        const elapsed = Date.now() - startTime;
                        const remainingTime = Math.max(0, MIN_LOADER_TIME - elapsed);
                        setTimeout(() => {
                            this.hideLoader();
                            this.showError(error.message);
                        }, remainingTime);
                    } else {
                        console.warn('Auto-calculation failed:', error.message);
                    }
                }
            });
        });
    }

    handleSubmit(e) {
        if (e) {
            e.preventDefault();
        }

        if (!this.validateAllSections()) {
            this.showError('Please complete all required fields');
            return;
        }

        this.performCalculation(true);
    }

    displayResults(results) {
        const maintainEl = document.getElementById('maintain-wattage');
        const heatUpEl = document.getElementById('heat-up-wattage');
        const flowEl = document.getElementById('flow-wattage');
        const tempCEl = document.getElementById('temp-c');
        const tempFEl = document.getElementById('temp-f');

        if (maintainEl) maintainEl.textContent = `${results.maintainWattage} W`;
        if (heatUpEl) heatUpEl.textContent = `${results.heatUpWattage} W`;
        if (flowEl) flowEl.textContent = `${results.flowWattage} W`;
        if (tempCEl) tempCEl.textContent = `${results.surfaceTempC} °C`;
        if (tempFEl) tempFEl.textContent = `${results.surfaceTempF} °F`;

        const resultsSection = document.getElementById('results-section');
        if (resultsSection) {
            resultsSection.scrollIntoView({behavior: 'smooth'});
        }
    }

    showLoader() {
        const loader = document.getElementById('calculator-loader');
        if (loader) loader.style.display = 'flex';
    }

    hideLoader() {
        const loader = document.getElementById('calculator-loader');
        if (loader) loader.style.display = 'none';
    }

    showError(message) {
        const errorBox = document.querySelector('.error-control');
        const errorMessage = document.querySelector('.error-box-message');

        if (errorBox && errorMessage) {
            errorMessage.textContent = message;
            errorBox.style.display = 'block';
            errorBox.scrollIntoView({behavior: 'smooth'});

            const closeBtn = errorBox.querySelector('.close');
            if (closeBtn) {
                closeBtn.onclick = () => {
                    this.closeErrorBox();
                };
            }
        } else {
            alert(message);
        }
    }

    showFieldError(field, message) {
        if (!field) return;

        const fieldName = field.name || field.id;
        const errorContainer = this.form.querySelector(`[data-field-error="${fieldName}"]`);
        const errorLabel = errorContainer ? errorContainer.querySelector('.error-label') : null;

        if (errorLabel) {
            errorLabel.textContent = message || 'This is a required field.';
            errorContainer.style.display = 'block';
        }

        field.classList.add('error');
    }

    clearFieldError(field) {
        if (!field) return;

        const fieldName = field.name || field.id;
        const errorContainer = this.form.querySelector(`[data-field-error="${fieldName}"]`);

        if (errorContainer) {
            const errorLabel = errorContainer.querySelector('.error-label');
            if (errorLabel) {
                errorLabel.textContent = '';
            }
            errorContainer.style.display = 'none';
        }

        field.classList.remove('error');
    }

    /**
     * Prints only the calculator form by temporarily isolating it from the Shopify theme.
     * CSS-only print styles don't work reliably because Shopify themes have complex nested
     * structures that create blank pages even when hidden. This approach moves the calculator
     * to a dedicated container and hides everything else during print.
     */
    handlePrint() {
        const calculator = document.querySelector('.mainframe-wrapper');
        if (!calculator) {
            window.print();
            return;
        }

        const originalParent = calculator.parentElement;
        const originalNextSibling = calculator.nextSibling;

        const printContainer = document.createElement('div');
        printContainer.id = 'print-container';

        printContainer.appendChild(calculator);
        document.body.appendChild(printContainer);

        const hiddenElements = [];
        Array.from(document.body.children).forEach(child => {
            if (child !== printContainer && child.style.display !== 'none') {
                hiddenElements.push({element: child, display: child.style.display});
                child.style.display = 'none';
            }
        });

        const cleanup = () => {
            if (originalNextSibling) {
                originalParent.insertBefore(calculator, originalNextSibling);
            } else {
                originalParent.appendChild(calculator);
            }
            printContainer.remove();
            hiddenElements.forEach(({element, display}) => element.style.display = display);
        };

        window.addEventListener('afterprint', cleanup, {once: true});
        window.print();
        setTimeout(cleanup, 1000);
    }
}

if (typeof window !== 'undefined') {
    window.HeatLossCalculator = HeatLossCalculator;
    new HeatLossCalculator();
}
